import Phaser from "phaser";
import { PALETTE } from "@/palette";

/**
 * The v0.7 "juice" — particle bursts + camera effects for the fixed beat set
 * (RFC §3). Pure presentation: driven off the TickEvents the scenes already
 * consume, no sim coupling. Tints come from the 10-color PALETTE (hard contract).
 * Phaser 4 particle API (`add.particles` → `explode`) + camera FX per the
 * particles / cameras skills.
 */

const DOT = "juice-dot";

/** Bake the one small particle texture (a soft dot) once per scene. */
export function bakeJuiceTextures(scene: Phaser.Scene): void {
  if (scene.textures.exists(DOT)) return;
  const g = scene.add.graphics();
  g.fillStyle(0xffffff, 1);
  g.fillCircle(8, 8, 8);
  g.generateTexture(DOT, 16, 16);
  g.destroy();
}

/** A short-lived burst emitter that self-destroys after its particles die. */
function burst(scene: Phaser.Scene, x: number, y: number, config: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig, count: number, life: number): void {
  const p = scene.add.particles(x, y, DOT, { ...config, emitting: false });
  p.setDepth(4000);
  p.explode(count);
  scene.time.delayedCall(life + 250, () => p.destroy());
}

/** Cash rains up-then-down (graduation payout + broker sale — DESIGN). */
export function cashRain(scene: Phaser.Scene, x: number, y: number): void {
  burst(
    scene,
    x,
    y,
    {
      tint: [PALETTE.gold, PALETTE.mustard],
      speedX: { min: -200, max: 200 },
      speedY: { min: -560, max: -300 },
      gravityY: 720,
      lifespan: 1050,
      scale: { start: 2.6, end: 0.9 }, // v0.15.2: bigger, fuller rain (playtest)
      alpha: { start: 1, end: 0 },
    },
    30,
    1050,
  );
}

/** A small flower burst on a death cot (DESIGN "empty cot, small flower"). */
export function flowerBurst(scene: Phaser.Scene, x: number, y: number): void {
  burst(
    scene,
    x,
    y,
    {
      tint: [PALETTE.coralRed, PALETTE.green, PALETTE.cream],
      speed: { min: 40, max: 150 },
      lifespan: 1400,
      scale: { start: 0.9, end: 0 },
      alpha: { start: 1, end: 0 },
      gravityY: 120,
    },
    14,
    1400,
  );
}

/** Debris burst for the federal raid. */
export function debris(scene: Phaser.Scene, x: number, y: number): void {
  burst(
    scene,
    x,
    y,
    {
      tint: [PALETTE.charcoal, PALETTE.gray, PALETTE.coralRed],
      speed: { min: 120, max: 400 },
      lifespan: 1100,
      scale: { start: 1.2, end: 0 },
      alpha: { start: 1, end: 0 },
      gravityY: 520,
    },
    26,
    1100,
  );
}

// ── camera effects (thin wrappers over the Phaser 4 camera FX) ──────────────

export function shake(scene: Phaser.Scene, ms = 300, intensity = 0.012): void {
  scene.cameras.main.shake(ms, intensity);
}

/** A colored screen flash — default the coral-red game-over alarm. */
export function flash(scene: Phaser.Scene, rgb: [number, number, number] = [226, 102, 78], ms = 350): void {
  scene.cameras.main.flash(ms, rgb[0], rgb[1], rgb[2]);
}

/** The mailbox auto-travel "camera focus" (ADR-001) — a gentle white arrival pulse. */
export function focusPulse(scene: Phaser.Scene): void {
  scene.cameras.main.flash(180, 255, 255, 255);
}
